import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Save } from 'lucide-react';
import { useCreateRole } from '../hooks/useRoles';

const permissionsGroups = [
  {
    groupName: 'صلاحيات الإدارة والنظام (Admin & System)',
    permissions: [
      { key: 'All', name: 'جميع الصلاحيات (All Permissions)', description: 'منح حق الوصول الكامل لجميع وظائف النظام' },
      { key: 'admin:system:read', name: 'قراءة النظام', description: 'عرض معلومات النظام والبنية التحتية والخوادم' },
      { key: 'admin:system:write', name: 'تعديل النظام', description: 'التحكم في إعدادات النظام وتحديث البنية التحتية' },
      { key: 'admin:user-roles:read', name: 'قراءة أدوار المستخدمين', description: 'عرض تعيينات الأدوار والصلاحيات للمستخدمين' },
      { key: 'admin:user-roles:write', name: 'تعديل أدوار المستخدمين', description: 'تعديل أو إلغاء تعيينات الأدوار والصلاحيات للمستخدمين' },
      { key: 'admin:users:read', name: 'قراءة المستخدمين', description: 'الوصول لقائمة المستخدمين وملفاتهم الشخصية في النظام' },
      { key: 'admin:users:write', name: 'تعديل المستخدمين', description: 'إضافة، تعديل أو حذف حسابات المستخدمين وملفاتهم' },
      { key: 'admin:settings:read', name: 'قراءة إعدادات النظام', description: 'عرض الإعدادات العامة للشركة والتفضيلات الافتراضية' },
      { key: 'admin:settings:write', name: 'تعديل إعدادات النظام', description: 'تعديل الإعدادات الأساسية للشركة والتفضيلات الافتراضية' },
      { key: 'admin:logs:read', name: 'قراءة سجلات النظام (Audit Logs)', description: 'مراجعة السجلات التاريخية لأنشطة المستخدمين والتغييرات' },
      { key: 'admin:backup:write', name: 'تعديل النسخ الاحتياطي', description: 'إنشاء نسخ احتياطية أو استعادة البيانات من نسخة سابقة' },
      { key: 'admin:security:write', name: 'تعديل أمن النظام', description: 'التحكم في سياسات الأمان، الجدران النارية، وقواعد الحماية' },
      { key: 'admin:read', name: 'قراءة عامة للإدارة', description: 'وصول للقراءة فقط لجميع أقسام لوحة الإدارة' },
      { key: 'admin:write', name: 'كتابة عامة للإدارة', description: 'وصول للتعديل والكتابة في جميع أقسام لوحة الإدارة' }
    ]
  },
  {
    groupName: 'صلاحيات الأقسام الجديدة (New Modules)',
    permissions: [
      { key: 'shipping:orders:read', name: 'عرض طلبات المبيعات', description: 'الوصول لقائمة طلبات المبيعات وفواتيرها التشغيلية' },
      { key: 'shipping:vans:read', name: 'عرض سيارات التوزيع', description: 'الوصول لبيانات وقائمة سيارات التوزيع والفانات المتاحة' },
      { key: 'shipping:banks:read', name: 'عرض الحسابات البنكية', description: 'الوصول لإدارة الحسابات البنكية والبيانات المالية المرتبطة' }
    ]
  },
  {
    groupName: 'صلاحيات التقارير والتحليلات (Reports & Analytics)',
    permissions: [
      { key: 'reports:read', name: 'قراءة التقارير', description: 'عرض التقارير المنشأة مسبقاً في النظام' },
      { key: 'reports:write', name: 'تعديل التقارير', description: 'تحرير أو تحديث محتوى التقارير الموجودة' },
      { key: 'reports:create', name: 'إنشاء تقرير جديد', description: 'إنشاء تقارير مخصصة جديدة بناءً على بيانات النظام' },
      { key: 'reports:export', name: 'تصدير التقارير', description: 'تصدير التقارير بصيغ مختلفة (Excel، PDF، CSV)' },
      { key: 'reports:schedule', name: 'جدولة التقارير', description: 'إعداد التقارير لترسل تلقائياً بفترات زمنية محددة' },
      { key: 'reports:share', name: 'مشاركة التقارير', description: 'مشاركة التقارير مع المستخدمين أو الأقسام المحددة' },
      { key: 'analytics:read', name: 'قراءة التحليلات التشغيلية', description: 'الوصول للتحليلات التشغيلية ولوحات المؤشرات الرئيسية' },
      { key: 'dashboard:read', name: 'قراءة لوحة المؤشرات', description: 'عرض لوحة المؤشرات الرئيسية ونظرة عامة على أداء النظام' },
      { key: 'data:export', name: 'تصدير البيانات الخام', description: 'تصدير البيانات الخام لاستخدامها في أدوات تحليل خارجية' },
      { key: 'insights:read', name: 'قراءة الرؤى التنبؤية', description: 'عرض التحليلات التنبؤية والرؤى المستقبلية من البيانات' }
    ]
  }
];

export function CreateRolePage() {
  const navigate = useNavigate();
  const createRoleMutation = useCreateRole();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // هنا قمنا بتحويل الكائن بالكامل إلى any لتخطي قيود الـ TypeScript الصارمة
      await createRoleMutation.mutateAsync({
        name,
        description,
        isActive,
        permissions: selectedPermissions,
      } as any);
      navigate('/roles');
    } catch (error) {
      console.error('Error creating role:', error);
    }
  };

  const handlePermissionChange = (key: string) => {
    setSelectedPermissions(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSelectGroupAll = (keys: string[], isAllSelected: boolean) => {
    if (isAllSelected) {
      setSelectedPermissions(prev => prev.filter(k => !keys.includes(k)));
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...keys])]);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate('/roles')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 transform rotate-180" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">إنشاء دور جديد</h1>
            <p className="text-sm text-slate-500 mt-1">تعديل مجموعة الصلاحيات المعرفة في النظام لتكوين الدور</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">اسم الدور (Role Key / Name) *</label>
            <input
              type="text"
              required
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-slate-300 focus:outline-none focus:border-blue-500"
              placeholder="مثال: manager أو shipping_agent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">الحالة (Status)</label>
            <div className="flex items-center h-11">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                <span className="mr-3 text-sm font-medium text-slate-700 px-2">مفعل</span>
              </label>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">الوصف (Description)</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="w-full p-3 rounded-lg border border-slate-300 focus:outline-none focus:border-blue-500"
            rows={3}
            placeholder="اكتب وصفاً مهماً لمهام هذا الدور في النظام..."
          />
        </div>

        <div className="border-t border-slate-200 pt-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <Shield size={20} className="text-orange-500" />
              <h2 className="text-lg font-bold text-slate-900">مصفوفة صلاحيات النظام (API Slugs Matrix)</h2>
            </div>
            <span className="text-sm bg-slate-100 text-slate-700 px-3 py-1 rounded-full">
              تم اختيار ({selectedPermissions.length}) من صلاحيات API
            </span>
          </div>

          <div className="space-y-6">
            {permissionsGroups.map((group, gIdx) => {
              const groupKeys = group.permissions.map(p => p.key);
              const isAllGroupSelected = groupKeys.every(k => selectedPermissions.includes(k));

              return (
                <div key={gIdx} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                  <div className="flex justify-between items-center mb-3 pb-2 border-b border-slate-200">
                    <h3 className="font-bold text-slate-800">{group.groupName}</h3>
                    <button
                      type="button"
                      onClick={() => handleSelectGroupAll(groupKeys, isAllGroupSelected)}
                      className="text-xs text-blue-600 hover:underline font-semibold"
                    >
                      {isAllGroupSelected ? 'إلغاء تحديد المجموعة' : 'تحديد كل المجموعة'}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {group.permissions.map((perm, pIdx) => {
                      const isSelected = selectedPermissions.includes(perm.key);
                      return (
                        <div key={pIdx} className="flex items-start justify-between p-3 bg-white border border-slate-200 rounded-lg shadow-sm">
                          <div className="ml-3">
                            <p className="text-sm font-semibold text-slate-800">{perm.name}</p>
                            <code className="text-[11px] text-blue-600 bg-blue-50 px-1 rounded font-mono">{perm.key}</code>
                            <p className="text-[11px] text-slate-400 mt-1" title={perm.description}>{perm.description}</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handlePermissionChange(perm.key)}
                            className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
          <button
            type="button"
            onClick={() => navigate('/roles')}
            className="px-4 h-11 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium"
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={createRoleMutation.isPending}
            className="px-5 h-11 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium shadow-sm disabled:bg-blue-400"
          >
            <Save className="h-4 w-4" />
            <span>حفظ الدور الجديد</span>
          </button>
        </div>
      </form>
    </div>
  );
}
